import { useClassStore } from '@Utils/useClassStore';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { inject, injectable } from 'inversify';
import { action, computed, observable } from 'mobx';
import { Platform } from 'react-native';

import AuthAPI from '~gateways/auth';
import container from '~ioc/ioc';
import SmartBrowserStore from '~stores/smart-browser';
import type { SignUpMethods } from '~types/analytics';
import type { IError, IUser } from '~types/models-types';
import type { PendingAuthenticatedAction } from '~types/views-types';
import { PendingAuthenticationActionBlurbFallback } from '~types/views-types';

type TToggleLoginAction =
  | { setting: false }
  | ({ method: SignUpMethods } & { setting: true });

type SignUp = {
  confirmPassword: string;
  emailAddress: string;
  password: string;
  fullName?: string;
};

@injectable()
class AuthStore {
  @inject(SmartBrowserStore)
  private SmartBrowserStore!: SmartBrowserStore;
  @observable uiLoginPanelOpen = false;
  @observable currentUser: IUser | null = null;
  @observable error: IError | null = null;
  @observable signupError: string | null = null;
  @observable loginMethod: string | null = null;
  @observable PendingAuthActionLoginDialogBlurb: string =
    PendingAuthenticationActionBlurbFallback;
  @observable pendingAuthenticatedAction: PendingAuthenticatedAction | null =
    null;

  @action public readonly toggleLogin = ({
    setting,
    // @ts-expect-error - This exists if `setting` is true
    method,
  }: TToggleLoginAction) => {
    if (setting) {
      this.uiLoginPanelOpen = true;
      this.loginMethod = method;
      return;
    }
    this.uiLoginPanelOpen = false;
    this.loginMethod = null;
  };

  @action public setSignupError = (newState: string | null = null) => {
    this.signupError = newState;
  };

  @action public setCurrentUser = (user: IUser | null) =>
    (this.currentUser = user);

  @action public readonly logOut = async () => {
    await AuthAPI.logout();
    this.SmartBrowserStore.savedPreferences = null;
    this.setCurrentUser(null);
  };

  @action setPendingAuthActionLoginDialogBlurb = (
    newState: string = PendingAuthenticationActionBlurbFallback
  ) => {
    this.PendingAuthActionLoginDialogBlurb = newState;
  };

  @computed get isAuthenticated(): boolean {
    return Boolean(this.currentUser);
  }

  public authCheck = async (): Promise<any> => {
    const authFunc =
      Platform.OS === 'web' ? this.authFromCookies : this.authFromJwt;

    const user = await authFunc();
    if (user) {
      this.setCurrentUser(user);
    }

    return user;
  };

  public async authFromCookies(): Promise<any> {
    return AuthAPI.authCheckAsync();
  }

  public async authFromJwt(): Promise<any> {
    const tokens = await AsyncStorage.getItem('tokens');
    if (!tokens) {
      return;
    }

    return AuthAPI.authCheckAsyncNative(JSON.parse(tokens));
  }

  public async authenticateAsync(
    username: string,
    password: string
  ): Promise<any> {
    const response = await AuthAPI.authenticateAsync(username, password);
    if (response?.errorCode) {
      return response;
    }

    await AsyncStorage.setItem(
      'tokens',
      JSON.stringify({
        bearer: response?.bearerToken,
        refresh: response?.refreshToken,
      })
    );

    this.SmartBrowserStore.postLogin().then();

    return response;
  }

  public async signUp({
    confirmPassword,
    emailAddress,
    password,
    fullName,
  }: SignUp) {
    return AuthAPI.SignupFromLogin({
      confirmPassword,
      emailAddress,
      fullName,
      password,
    });
  }

  requestPasswordReset = (email: string): Promise<void> =>
    AuthAPI.requestPasswordReset(email);

  acceptPasswordReset = (params: {
    password: string;
    confirmPassword: string;
    token: string;
  }): Promise<void> => AuthAPI.acceptPasswordReset(params);
}

export const useAuthStore = () =>
  useClassStore<AuthStore>(container.get(AuthStore));

export default AuthStore;
